'use client';

import classNames from 'classnames';
import Image from 'next/image';
import { useEffect, useMemo, useState } from 'react';
import { useMedia } from 'react-use';

interface OverlayImage {
  alt: string;
  className?: string;
  height?: number;
  parallaxStrength?: number;
  src: string;
  width?: number;
}

interface ParallaxOverlayImagesProps {
  images: OverlayImage[];
  isLoaded?: boolean;
}

export function ParallaxOverlayImages({ images, isLoaded = false }: ParallaxOverlayImagesProps) {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const is4k = useMedia('(min-width: 2400px)', false);

  // Generate consistent variations based on index (deterministic)
  const movementVariations = useMemo(
    () =>
      images.map((_, index) => {
        // Use index to create deterministic but different values
        const seed = index * 123.456; // Simple seed
        const xMultiplier = 0.5 + (Math.sin(seed) * 0.5 + 0.5) * 1; // 0.5 to 1.5
        const yMultiplier = 0.3 + (Math.cos(seed * 1.5) * 0.5 + 0.5) * 0.8; // 0.3 to 1.1
        const xDirection = Math.sin(seed * 2) > 0 ? 1 : -1;
        const yDirection = Math.cos(seed * 3) > 0 ? 1 : -1;

        return { xMultiplier, yMultiplier, xDirection, yDirection };
      }),
    [images],
  );

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      const x = (e.clientX / window.innerWidth - 0.5) * 2;
      const y = (e.clientY / window.innerHeight - 0.5) * 2;
      setMousePosition({ x, y });
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  return (
    <>
      {images.map((image, index) => {
        const strength = image.parallaxStrength || 0.2;
        const variation = movementVariations[index];

        const range = 50 + index * 30 + Math.sin(index * 2.5) * 100;
        const offsetX = mousePosition.x * variation.xDirection * strength * variation.xMultiplier * range;
        const offsetY = mousePosition.y * variation.yDirection * strength * variation.yMultiplier * (range * 0.6);

        return (
          <div
            key={index}
            className={classNames('absolute transition-transform duration-100 ease-out', image.className)}
            style={{
              transform: `translate(${offsetX}px, ${offsetY}px) ${is4k ? 'scale(200%)' : ''}`,
            }}
          >
            <Image
              src={image.src}
              alt={image.alt}
              width={image.width || 200}
              height={image.height || 200}
              className={`object-contain transition-opacity duration-500 ${isLoaded ? 'opacity-100' : 'opacity-0'}`}
            />
          </div>
        );
      })}
    </>
  );
}
